import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { userAPI, itemAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Heart, MapPin, Bookmark } from 'lucide-react';

const SavedItems = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
      return;
    }
    fetchSavedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate]);

  const fetchSavedItems = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getSavedItems(user._id);
      console.log('✅ Saved items fetched successfully:', response.data);
      setItems(response.data.data || []);
    } catch (err) {
      setError('Failed to load saved items');
      console.error('❌ Error fetching saved items:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container" style={{ paddingTop: '24px', paddingBottom: '40px' }}>
      <h1 style={{ marginBottom: '24px', fontSize: '28px' }}>
        Saved Items
      </h1>

      {error && <div className="error-message">{error}</div>}

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Bookmark size={48} style={{ margin: '0 auto 16px', color: 'var(--textSecondary)' }} />
          <h2 style={{ marginBottom: '16px' }}>No saved items</h2>
          <p className="text-muted" style={{ marginBottom: '24px' }}>
            Items you save will appear here
          </p>
          <Link to="/" className="btn btn-primary">
            Browse Items
          </Link>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '24px',
          }}
        >
          {items.map((item) => (
            <ItemCard key={item._id} item={item} onUnsave={fetchSavedItems} />
          ))}
        </div>
      )}
    </div>
  );
};

const ItemCard = ({ item, onUnsave }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  // Initialize like state
  useEffect(() => {
    if (isAuthenticated && user) {
      setLiked(item.likes?.includes(user._id) || false);
    }
    setLikeCount(item.likes?.length || 0);
  }, [item, user, isAuthenticated]);

  // Handle like toggle
  const handleLikeToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (liked) {
        await itemAPI.unlikeItem(item._id);
        setLiked(false);
        setLikeCount((prev) => prev - 1);
      } else {
        await itemAPI.likeItem(item._id);
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  // Handle unsave
  const handleUnsave = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await itemAPI.unsaveItem(item._id);
      // Refresh the saved items list
      if (onUnsave) {
        onUnsave();
      }
    } catch (err) {
      console.error('Failed to unsave item:', err);
    }
  };

  // Helper to get image URL from various formats
  const getImageUrl = (item) => {
    // Handle images array (objects with fullSize/thumbnail)
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      const primaryImg = item.images.find(img => img.isPrimary) || item.images[0];

      if (typeof primaryImg === 'object') {
        return primaryImg.thumbnail || primaryImg.fullSize || primaryImg.url;
      }
      if (typeof primaryImg === 'string') {
        return primaryImg;
      }
    }

    // Handle direct thumbnail/image properties
    if (item.thumbnail) return item.thumbnail;
    if (item.image) return item.image;

    // Fallback placeholder
    return `https://via.placeholder.com/400x400?text=${encodeURIComponent(item.title || 'No Image')}`;
  };

  const imageUrl = getImageUrl(item);

  return (
    <Link to={`/items/${item._id}`} className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <div style={{ position: 'relative', paddingBottom: '100%', backgroundColor: 'var(--secondary)' }}>
        <img
          src={imageUrl}
          alt={item.title}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/400x400?text=Image+Error';
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* Like button with count */}
        <button
          onClick={handleLikeToggle}
          style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            borderRadius: '20px',
            padding: '6px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm)',
            zIndex: 10,
          }}
          title="Like this item"
        >
          <Heart
            size={18}
            fill={liked ? '#e60023' : 'none'}
            color={liked ? '#e60023' : '#666'}
          />
          {likeCount > 0 && (
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
              {likeCount}
            </span>
          )}
        </button>

        {/* Unsave button */}
        <button
          onClick={handleUnsave}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-sm)',
            zIndex: 10,
          }}
          title="Remove from saved"
        >
          <Bookmark
            size={18}
            fill="#2196F3"
            color="#2196F3"
          />
        </button>

        {item.condition && (
          <span
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '12px',
              background: 'var(--cardBackground)',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '600',
              boxShadow: 'var(--shadow-sm)',
            }}
          >
            {item.condition}
          </span>
        )}
      </div>

      <div style={{ padding: '16px' }}>
        <h3
          style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '8px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>
            ${item.price}
          </span>
          {item.originalPrice && item.originalPrice > item.price && (
            <span
              style={{
                fontSize: '14px',
                color: 'var(--textSecondary)',
                textDecoration: 'line-through',
              }}
            >
              ${item.originalPrice}
            </span>
          )}
        </div>

        {item.location?.city && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--textSecondary)', fontSize: '14px' }}>
            <MapPin size={14} />
            <span>{item.location.city}, {item.location.state}</span>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
          <img
            src={item.user?.profileImage || `https://ui-avatars.com/api/?name=${item.user?.username}&size=32`}
            alt={item.user?.username}
            style={{ width: '24px', height: '24px', borderRadius: '50%' }}
          />
          <span style={{ fontSize: '14px', color: 'var(--textSecondary)' }}>
            {item.user?.username}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default SavedItems;
